import { useEffect, useRef, useState, useCallback } from 'react';

export const useReveal = (options = { threshold: 0.05, rootMargin: "0px 0px 0px 0px" }) => {
    const [isVisible, setIsVisible] = useState({});
    const observers = useRef({});

    const setRef = useCallback((key) => (el) => {
        if (!el) return;

        // Clean up previous observer for this key if it exists
        if (observers.current[key]) {
            observers.current[key].disconnect();
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible((prev) => ({
                    ...prev,
                    [key]: entry.isIntersecting,
                }));
            },
            options
        );

        observer.observe(el);
        observers.current[key] = observer;
    }, [options]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            Object.values(observers.current).forEach((observer) => {
                if (observer) observer.disconnect();
            });
            observers.current = {}; // Reset the map
        };
    }, []);

    return { setRef, isVisible };
};

/**
 * useParallax Hook (unchanged - looks fine)
 */
export const useParallax = (speed = 0.15) => {
    const ref = useRef(null);
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReduced) return;

        let frame = null;

        const handleScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                if (ref.current) {
                    const rect = ref.current.getBoundingClientRect();
                    const viewportCenter = window.innerHeight / 2;
                    const elementCenter = rect.top + rect.height / 2;
                    const distance = elementCenter - viewportCenter;
                    setOffset(distance * speed * -1);
                }
                frame = null;
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [speed]);

    return [ref, offset];
};